import { Flex } from '@osmosis/tailwind-ui/components/Box';
import Heading from '../block-ui/Heading';
import RichText from '../block-ui/RichText';
import { StatItemProps } from '../../../types/composer';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  getTailwindBorderColorForTheme,
  getTailwindTextColorForTheme,
  themeToTailwindClassMap,
} from '../../../lib/composer/schemas/theme';

type StatItemsProps = {
  stats: StatItemProps[];
  theme?: string;
};

/**
 * StatItem component.
 *
 * Displays a single statistic with a value, type (e.g. %, K), title, and description.
 * @component
 * @param {StatItemProps} props - The props for the StatItem component.
 * @param {number|string} props.value - The main value to display (e.g. 87).
 * @param {string} props.type - The unit or type for the value (e.g. %, x).
 * @param {string} [props.title] - The title of the statistic.
 * @param {string} [props.description] - The description or additional info.
 * @returns {JSX.Element} The rendered StatItem component.
 */
const StatItem = (props: StatItemProps) => {
  const {
    value,
    type,
    title,
    description,
    statColor = 'transparent',
    theme = 'transparent',
  } = props;
  const bgColor = themeToTailwindClassMap[statColor];
  const textColor = getTailwindTextColorForTheme(statColor);
  const borderColor = getTailwindBorderColorForTheme(statColor);
  return (
    <Flex className={cn('flex items-center justify-center')}>
      <Flex
        className={cn(
          'rounded-full border w-24 h-24 items-center justify-center flex-shrink-0',
          bgColor,
          borderColor,
        )}
      >
        <Flex className={cn('items-baseline', textColor)}>
          <P>
            <span className={`text-5xl font-bold`}>{value}</span>
            <span className="text-xl">{type}</span>
          </P>
        </Flex>
      </Flex>
      <Flex className="ml-4 flex flex-col">
        <Heading text={title ?? ''} theme={theme} />
        <RichText
          html={description ?? ''}
          containerProps={{ className: 'text-sm' }}
          theme={theme}
        />
      </Flex>
    </Flex>
  );
};

/**
 * StatItems component.
 *
 * Renders a grid of StatItem components.
 * @component
 * @param {Object} props - The props for the StatItems component.
 * @param {StatItemProps[]} props.stats - Array of stat item data to display.
 * @returns {JSX.Element} The rendered StatItems grid.
 */
const StatItems = ({ stats = [], theme }: StatItemsProps) => {
  return (
    <Flex className={`grid grid-cols-2 gap-8 justify-items-start mt-10`}>
      {stats.map((stat, i) => (
        <StatItem key={`${stat.title ?? ''}${i}`} theme={theme} {...stat} />
      ))}
    </Flex>
  );
};

export default StatItems;
